import { useEffect, useRef, useState } from "react";
import { RotateCcw, Send, X } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";

type ChatRole = "user" | "assistant";

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
};

function FloAvatar({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M30 9l16 5.2v13.4c0 11-6.8 18.6-16 22-9.2-3.4-16-11-16-22V14.2L30 9z"
        fill="#E7A33B"
      />
      <circle cx="24.5" cy="26" r="2.4" fill="#1E3A5F" />
      <circle cx="35.5" cy="26" r="2.4" fill="#1E3A5F" />
      <path
        d="M24.5 33c2 2.2 9 2.2 11 0"
        stroke="#1E3A5F"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

const MAX_MESSAGES = 30;
const MAX_MESSAGE_CHARS = 4000;
const MAX_TOTAL_CHARS = 24000;
const MAX_BODY_BYTES = 64 * 1024;

const HIDDEN_PATHS = new Set([
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/admin-dashboard",
  "/internal-management",
  "/customer-management",
]);

function shouldHideWidget(pathname: string) {
  return (
    HIDDEN_PATHS.has(pathname) ||
    pathname.startsWith("/admin/company/")
  );
}

function getOpeningGreeting(isAuthenticated: boolean) {
  return isAuthenticated
    ? "Hi, I'm Flo. How can I help you today?"
    : "Hi, I'm Flo. What brings you to NightKept today?";
}

const PROSPECT_STARTERS = [
  "What is NightKept?",
  "Do I need any hardware?",
  "How much does it cost?",
];

const EMPLOYER_STARTERS = [
  "How do I add a guard?",
  "How do I add a checkpoint?",
  "How do I see a guard's patrol route?",
];

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

export function FloSupportWidget() {
  const [location] = useLocation();
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [inlineMessage, setInlineMessage] = useState<string | null>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setShowNudge(true), 3500);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    inputRef.current?.focus();
    threadRef.current?.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !threadRef.current) {
      return;
    }

    threadRef.current.scrollTo({
      top: threadRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, isSending]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        launcherRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  if (shouldHideWidget(location)) {
    return null;
  }

  const openWidget = () => {
    setShowNudge(false);
    setInlineMessage(null);
    setIsOpen(true);

    if (messages.length === 0) {
      setMessages([
        {
          id: "greeting",
          role: "assistant",
          content: getOpeningGreeting(isAuthenticated),
        },
      ]);
    }
  };

  const closeWidget = () => {
    setIsOpen(false);
    launcherRef.current?.focus();
  };

  const startNewChat = () => {
    setMessages([
      {
        id: `greeting-${Date.now()}`,
        role: "assistant",
        content: getOpeningGreeting(isAuthenticated),
      },
    ]);
    setDraft("");
    setInlineMessage(null);
    inputRef.current?.focus();
  };

  const sendMessage = async (messageOverride?: string) => {
    const content = (messageOverride ?? draft).trim();
    if (!content || isSending) {
      return;
    }

    if (content.length > MAX_MESSAGE_CHARS) {
      setInlineMessage("That message is too long. Please keep it under 4,000 characters.");
      return;
    }

    const nextMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
    };
    const nextMessages = [...messages, nextMessage];
    const payloadMessages = nextMessages.map(({ role, content: messageContent }) => ({
      role,
      content: messageContent,
    }));
    const totalChars = payloadMessages.reduce(
      (total, message) => total + message.content.length,
      0,
    );
    const payloadBytes = new TextEncoder().encode(
      JSON.stringify({ messages: payloadMessages }),
    ).length;

    if (
      nextMessages.length > MAX_MESSAGES ||
      totalChars > MAX_TOTAL_CHARS ||
      payloadBytes > MAX_BODY_BYTES
    ) {
      setInlineMessage(
        "This conversation has reached its limit. Start a new chat to keep going.",
      );
      return;
    }

    setMessages(nextMessages);
    setDraft("");
    setInlineMessage(null);
    setIsSending(true);

    try {
      const result = await apiRequest<{ reply: string }>(
        "POST",
        "/api/support/chat",
        { messages: payloadMessages },
      );

      if (!result || typeof result.reply !== "string" || result.reply.trim().length === 0) {
        throw new Error("The support reply was empty");
      }

      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: result.reply,
        },
      ]);
    } catch (error) {
      setInlineMessage(
        getErrorStatus(error) === 429
          ? "I've had a lot of questions just now. Please give it a moment and try again."
          : "Sorry, I hit a snag. Please try again.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void sendMessage();
    }
  };

  const starterPrompts = isAuthenticated
    ? EMPLOYER_STARTERS
    : PROSPECT_STARTERS;
  const showStarterPrompts =
    messages.length === 1 &&
    messages[0]?.role === "assistant" &&
    !isSending;

  return (
    <>
      {isOpen ? (
        <section
          id="flo-support-panel"
          className="fixed inset-x-3 bottom-3 z-50 flex max-h-[min(680px,calc(100dvh-1.5rem))] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[360px]"
          role="dialog"
          aria-label="Flo NightKept support chat"
          aria-describedby="flo-support-subtitle"
        >
          <header className="flex items-center justify-between bg-[var(--patrol-navy)] px-4 py-3 text-white">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#254a75]"
                aria-hidden="true"
              >
                <FloAvatar size={30} />
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#254a75] bg-green-500" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold">Flo</h2>
                <p id="flo-support-subtitle" className="truncate text-xs text-slate-200">
                  Here to help, usually replies quickly
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeWidget}
              className="rounded-lg p-2 text-slate-200 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#E7A33B]/50"
              aria-label="Close Flo support chat"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </header>

          <div
            ref={threadRef}
            className="min-h-0 max-h-[min(420px,calc(100dvh-14rem))] space-y-4 overflow-y-auto bg-slate-50 px-3 py-4 sm:px-4"
            aria-live="polite"
            aria-label="Conversation"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[88%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                    message.role === "user"
                      ? "rounded-br-md bg-[var(--patrol-navy)] text-white"
                      : "rounded-bl-md border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {message.content}
                </div>
              </div>
            ))}

            {showStarterPrompts ? (
              <div className="space-y-2 pt-1" aria-label="Suggested questions">
                <p className="px-1 text-xs font-medium text-slate-500">
                  Try asking
                </p>
                <div className="flex flex-wrap gap-2">
                  {starterPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => void sendMessage(prompt)}
                      disabled={isSending}
                      className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-[var(--patrol-navy)] shadow-sm transition hover:border-[#E7A33B] hover:bg-[#FBF3E4] focus:outline-none focus:ring-2 focus:ring-[#E7A33B]/50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {isSending ? (
              <div className="flex justify-start" role="status" aria-label="Flo is typing">
                <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" />
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-200 bg-white p-3">
            {inlineMessage ? (
              <div className="mb-2 flex items-start justify-between gap-2 rounded-lg bg-orange-50 px-3 py-2 text-xs leading-relaxed text-orange-900">
                <span>{inlineMessage}</span>
                {inlineMessage.includes("reached its limit") ? (
                  <button
                    type="button"
                    onClick={startNewChat}
                    className="shrink-0 font-semibold underline underline-offset-2 hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#E7A33B]"
                  >
                    Start new chat
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleInputKeyDown}
                placeholder="Ask Flo anything..."
                rows={1}
                maxLength={MAX_MESSAGE_CHARS}
                disabled={isSending}
                className="max-h-32 min-h-11 flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#E7A33B] focus:ring-2 focus:ring-[#E7A33B]/20 disabled:bg-slate-50"
                aria-label="Message Flo"
              />
              <button
                type="button"
                onClick={() => void sendMessage()}
                disabled={isSending || draft.trim().length === 0}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E7A33B] text-white transition hover:bg-[#f0b355] focus:outline-none focus:ring-2 focus:ring-[#E7A33B]/50 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-400">
              Press Enter to send, Shift+Enter for a new line
            </p>
          </div>
        </section>
      ) : null}

      {!isOpen && showNudge ? (
        <div className="fixed bottom-5 right-[4.75rem] z-50 flex items-center gap-1 rounded-full border border-slate-200 bg-white py-1 pl-3 pr-1 text-sm font-medium text-[var(--patrol-navy)] shadow-lg">
          <button
            type="button"
            onClick={openWidget}
            className="rounded-full py-1.5 focus:outline-none focus:ring-2 focus:ring-[#E7A33B]/50"
          >
            Hi, I'm Flo
          </button>
          <button
            type="button"
            onClick={() => setShowNudge(false)}
            className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#E7A33B]/50"
            aria-label="Dismiss Flo message"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {!isOpen ? (
        <button
          ref={launcherRef}
          type="button"
          onClick={openWidget}
          className="fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#E7A33B] text-white shadow-xl transition hover:scale-105 hover:bg-[#f0b355] focus:outline-none focus:ring-4 focus:ring-[#E7A33B]/35 sm:bottom-6 sm:right-6"
          aria-label="Open Flo support chat"
          aria-expanded={false}
          aria-controls="flo-support-panel"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--patrol-navy)]">
            <FloAvatar size={28} />
          </span>
        </button>
      ) : null}

    </>
  );
}